import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { OrderHeader, OrderListFilter } from '@/lib/api-types'
import { formatDate, formatMoney } from '@/lib/format'
import { stageChipClasses } from '@/lib/status'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DataTable, type DataTableColumn } from '@/components/ui/data-table'
import { EmptyState } from '@/components/ui/empty-state'
import { ErrorState } from '@/components/ui/error-state'
import { PageHeader } from '@/components/ui/page-header'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { StatusChip } from '@/components/ui/status-chip'
import { Toolbar } from '@/components/ui/toolbar'
import { orderNumber } from './order-number'

const FILTERS: OrderListFilter[] = ['open', 'closed', 'all']

function parseFilter(raw: string | null): OrderListFilter {
  return raw === 'closed' || raw === 'all' ? raw : 'open'
}

export function OrdersListPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const filter = parseFilter(searchParams.get('status'))

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['orders', filter],
    queryFn: ({ signal }) => api.listOrders(filter, { signal }),
    staleTime: 15_000,
  })

  function setFilter(next: OrderListFilter) {
    const params = new URLSearchParams(searchParams)
    if (next === 'open') params.delete('status')
    else params.set('status', next)
    setSearchParams(params, { replace: true })
  }

  const counts = data?.counts
  const filterLabel = (f: OrderListFilter): string => {
    const base = f === 'open' ? 'Open' : f === 'closed' ? 'Closed' : 'All'
    return counts ? `${base} (${counts[f]})` : base
  }

  const columns: DataTableColumn<OrderHeader>[] = [
    {
      id: 'number',
      header: 'Order #',
      widthClassName: 'w-32',
      sortAccessor: (o) => orderNumber(o),
      cell: (o) => (
        <Link
          to={`/orders/${o.id}`}
          onClick={(event) => event.stopPropagation()}
          className="rounded-sm no-underline underline-offset-4 hover:text-brand hover:underline"
        >
          <OrderNumberCell order={o} />
        </Link>
      ),
    },
    {
      id: 'poNumber',
      header: 'PO number',
      flex: true,
      widthClassName: 'min-w-28',
      sortAccessor: (o) => (o.poNumber ?? '').toLowerCase(),
      cell: (o) => (
        <span className="text-sm text-inkMid" title={o.poNumber ?? undefined}>
          {o.poNumber ?? '—'}
        </span>
      ),
    },
    {
      id: 'stage',
      header: 'Status',
      nowrap: true,
      sortAccessor: (o) => o.stageLabel.toLowerCase(),
      cell: (o) => <StatusChip label={o.stageLabel} chipClasses={stageChipClasses(o.stage)} />,
    },
    {
      id: 'amount',
      header: 'Amount',
      align: 'right',
      widthClassName: 'w-28',
      nowrap: true,
      sortAccessor: (o) => o.amount ?? 0,
      defaultSortDir: 'desc',
      cell: (o) => formatMoney(o.amount, o.currency),
    },
    {
      id: 'requiredDate',
      header: 'Required',
      widthClassName: 'w-28',
      nowrap: true,
      sortAccessor: (o) => o.requiredDate ?? '',
      cell: (o) => <span className="text-sm text-inkMid">{formatDate(o.requiredDate)}</span>,
    },
    {
      id: 'lineCount',
      header: 'Lines',
      align: 'right',
      widthClassName: 'w-16',
      sortAccessor: (o) => o.lineCount,
      defaultSortDir: 'desc',
      cell: (o) => o.lineCount,
    },
  ]

  return (
    <div className="mx-auto max-w-data">
      <PageHeader
        title="Orders"
        description="Every order with Green Line, and where each one stands."
        actions={
          <Button asChild>
            <Link to="/purchasing/new">Start a purchase order request</Link>
          </Button>
        }
      />

      <Toolbar className="mt-6">
        <SegmentedControl
          label="Filter orders"
          value={filter}
          onChange={setFilter}
          options={FILTERS.map((f) => ({
            value: f,
            label: filterLabel(f),
            withDot: f === 'open',
          }))}
        />
      </Toolbar>

      <div className="mt-4">
        <DataTable<OrderHeader>
          columns={columns}
          rows={data?.orders ?? []}
          rowKey={(o) => o.id}
          onRowClick={(o) => navigate(`/orders/${o.id}`)}
          caption="Your orders with Green Line"
          loading={isPending}
          loadingLabel="Loading your orders…"
          error={
            isError ? (
              <ErrorState message="We couldn’t load your orders." onRetry={() => void refetch()} />
            ) : undefined
          }
          empty={
            <OrdersEmptyState filter={filter} counts={counts} onSeeAll={() => setFilter('all')} />
          }
          renderCard={(o) => <OrderCard order={o} />}
        />
      </div>
    </div>
  )
}

function OrdersEmptyState({
  filter,
  counts,
  onSeeAll,
}: {
  filter: OrderListFilter
  counts: { open: number; closed: number; all: number } | undefined
  onSeeAll: () => void
}) {
  const accountHasOrders = (counts?.all ?? 0) > 0

  if (!accountHasOrders) {
    return (
      <EmptyState title="No orders yet">
        Your order history with Green Line will appear here.{' '}
        <Link
          to="/purchasing/new"
          className="rounded-sm font-semibold text-brand underline-offset-4 hover:underline"
        >
          Start a purchase order request
        </Link>{' '}
        to place your first one.
      </EmptyState>
    )
  }

  return (
    <EmptyState
      title={filter === 'open' ? 'No open orders right now.' : 'No closed orders yet.'}
      action={
        <Button variant="quiet" size="sm" onClick={onSeeAll}>
          See all orders
        </Button>
      }
    />
  )
}

/** The order-number cell: mono DocNum (or SF name), plus a syncing hint. */
function OrderNumberCell({ order }: { order: OrderHeader }) {
  return (
    <span className="flex flex-col leading-tight">
      {/* Capped + truncated below xl: a sync-pending row renders the full SF
          opp name, which must not dictate the column width for every DocNum
          row (its PO number sits in the next column, so nothing is lost). */}
      <span
        className="max-w-36 truncate font-mono text-[0.8125rem] text-inkMid xl:max-w-none"
        title={orderNumber(order)}
      >
        {orderNumber(order)}
      </span>
      {order.syncPending ? (
        <span className="font-mono text-[0.625rem] uppercase tracking-eyebrow text-mute">
          Processing — order number to follow
        </span>
      ) : null}
    </span>
  )
}

/** Narrow-screen fallback: one tappable card per order. */
function OrderCard({ order }: { order: OrderHeader }) {
  return (
    <Link to={`/orders/${order.id}`} className="block no-underline">
      <Card
        padding="sm"
        className="transition-colors duration-[180ms] ease-editorial hover:border-mute hover:bg-cream"
      >
        <span className="flex items-baseline justify-between gap-3">
          <OrderNumberCell order={order} />
          <span className="font-mono text-[0.8125rem] text-inkMid">
            {formatMoney(order.amount, order.currency)}
          </span>
        </span>
        <span className="mt-2.5 flex flex-wrap items-center gap-2">
          <StatusChip label={order.stageLabel} chipClasses={stageChipClasses(order.stage)} />
          {order.poNumber ? <span className="text-xs text-mute">PO {order.poNumber}</span> : null}
        </span>
        <span className="mt-2 block text-xs text-mute">
          {order.lineCount === 1 ? '1 line' : `${order.lineCount} lines`}
          {order.requiredDate ? ` · required ${formatDate(order.requiredDate)}` : ''}
        </span>
      </Card>
    </Link>
  )
}
